/**
 * V2 Place — rendering and derivations. Split out of place.js on 2026-07-25
 * (488 lines, plan budget ~400).
 *
 * This file was easy to lift because renderPlace already took all seven of
 * its inputs as parameters rather than closing over mount's state — the place
 * view was written render-as-a-function-of-data from the start. Everything
 * here is the same: arguments in, HTML out. place.js re-exports the four
 * public derivations.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml, stripHtmlTags, truncate } from '../../../assets/js/utils.js';
import { slugify } from '../../../assets/js/shared/slugify.js';
import { icon, categoryIcon } from '../../ui/icons.js';
import { thumbUrl } from '../../ui/card.js';
import { shareButton } from '../../ui/share.js';
import { shareMenu } from '../../ui/share-menu.js';
import { sharePhotosButton } from '../../ui/share-photos.js';
import { PRICE_MAP } from './metadata.js';
import { saveControls } from '../lists/save-button.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function logDate(isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** Aggregate the stat stamps from the loaded logs. Exported for tests. */
export function computeStats(logs) {
  const explorers = new Set(logs.map((l) => l.user_id).filter(Boolean));
  return { yoinks: logs.length, explorers: explorers.size };
}

/**
 * Friends strip: people the signed-in user follows who have yoinked
 * here, derived from the already-loaded logs (no extra query).
 * Exported for tests.
 */
export function deriveFriends(logs, followingIds) {
  const following = new Set(followingIds || []);
  const seen = new Map();
  for (const log of logs) {
    const p = log.public_profiles;
    if (!p?.id || !following.has(p.id) || seen.has(p.id)) continue;
    seen.set(p.id, p);
  }
  return [...seen.values()];
}

function friendChip(profile) {
  const name = profile.full_name || profile.username;
  const avatar = profile.avatar_url
    ? `<img class="friend-avatar" src="${gateway.getPublicUrl('avatars', profile.avatar_url)}" alt="">`
    : `<span class="friend-avatar friend-avatar-blank" aria-hidden="true">${escapeHtml(name.charAt(0).toUpperCase())}</span>`;
  return `
    <button type="button" class="friend-chip" data-profile-username="${escapeHtml(profile.username)}">
      ${avatar}<span>${escapeHtml(name)}</span>
    </button>`;
}

function friendsStrip(logs, store) {
  if (!store.get().user) return '';
  const friends = deriveFriends(logs, store.get().followingIds);
  if (!friends.length) return '';
  return `
    <section class="place-friends" aria-label="People you follow who yoinked here">
      <h3 class="friends-title">Friends who’ve been here</h3>
      <div class="friends-row">${friends.map(friendChip).join('')}</div>
    </section>`;
}

function logThumb(log) {
  const images = Array.isArray(log.images) ? log.images : [];
  if (images.length && images[0]) {
    const img = images[0];
    const url = img.startsWith('http') ? img : gateway.getPublicUrl('yoinks', img);
    return `<img class="log-thumb" src="${thumbUrl(url, 320)}" alt="" loading="lazy"
      data-fallback-src="${url}" data-fallback-cat="${escapeHtml(log.categories?.name || '')}">`;
  }
  return `<div class="log-thumb log-thumb-blank">${categoryIcon(log.categories, 22)}</div>`;
}

function logEntry(log) {
  const username = log.public_profiles?.username || '';
  const who = log.public_profiles?.full_name || username || 'Anonymous';
  const note = log.description ? truncate(stripHtmlTags(log.description), 80) : '';
  // The row itself is a mouse-only convenience target: an interactive
  // element can't contain other interactive elements (WCAG 4.1.2 — same
  // rule as the yoink card), so the keyboard/SR entry points are the two
  // inner links (explorer, yoink) instead of a row-level role=button.
  return `
    <div class="log-entry" data-yoink-id="${log.id}">
      ${logThumb(log)}
      <div class="log-body">
        ${username ? `
        <span class="log-who log-who-link" data-log-user="${escapeHtml(username)}" role="link" tabindex="0"
          aria-label="View ${escapeHtml(who)}’s journal">${escapeHtml(who)}</span>`
        : `<div class="log-who">${escapeHtml(who)}</div>`}
        <span class="log-what log-what-link" data-yoink-id="${log.id}" role="link" tabindex="0"
          aria-label="View yoink: ${escapeHtml(log.title || 'Yoink')} by ${escapeHtml(who)}">${
            note ? `“${escapeHtml(note)}”` : escapeHtml(log.title || 'View yoink')}</span>
      </div>
      <div class="log-when">${log.personal_category_id
        ? '<span class="log-personal" title="Filed under a personal category">🔒</span> ' : ''}${logDate(log.created_at)}</div>
    </div>`;
}

/**
 * The place's face (V1 location-panel.js:323-366): a collage of up to 6
 * visitor photos when there are ≥3, a single photo hero for 1-2, the
 * pin stamp otherwise. Exported for tests.
 */
export function heroPhotos(logs) {
  const seen = new Set();
  for (const log of logs) {
    const images = Array.isArray(log.images) ? log.images : [];
    if (!images[0]) continue;
    const url = images[0].startsWith('http') ? images[0] : gateway.getPublicUrl('yoinks', images[0]);
    seen.add(url);
    if (seen.size >= 6) break;
  }
  return [...seen];
}

function heroHtml(logs, metadata) {
  const photos = heroPhotos(logs);
  if (photos.length >= 3) {
    return `
      <div class="place-map place-collage" role="img" aria-label="Photos from visitors">
        ${photos.map((url, i) => `<img src="${thumbUrl(url)}" alt="" loading="${i < 3 ? 'eager' : 'lazy'}"
          data-fallback-src="${url}">`).join('')}
      </div>`;
  }
  if (photos.length) {
    return `
      <div class="place-map place-photo-hero" role="img" aria-label="A photo from a visitor">
        <img src="${thumbUrl(photos[0], 1280)}" alt="" data-fallback-src="${photos[0]}">
      </div>`;
  }
  // Nobody has yoinked here yet — Google's photo (proxied, see
  // metadata.js) beats a blank pin stamp for a place someone saved
  // sight-unseen. The credit line is Places-policy required.
  if (metadata?.photo_url) {
    return `
      <div class="place-map place-photo-hero place-photo-google" role="img"
        aria-label="A photo of this place from Google">
        <img src="${escapeHtml(metadata.photo_url)}" alt="">
        <span class="place-photo-credit">Photo: ${
          metadata.photo_attribution ? `${escapeHtml(metadata.photo_attribution)} · ` : ''}Google</span>
      </div>`;
  }
  return `
      <div class="place-map" role="img" aria-label="Map placeholder">
        ${icon('pin', 44)}
      </div>`;
}

/**
 * V1's category filter pills (location-panel.js:426-558): shown when a
 * place spans >1 category; tapping filters the log list in place, and
 * tapping the ALREADY-active pill opens that category's page. Exported
 * for tests.
 */
export function deriveLogCategories(logs) {
  const byId = new Map();
  for (const log of logs) {
    const cat = log.categories;
    if (!cat?.id) continue;
    const entry = byId.get(cat.id) || { ...cat, count: 0 };
    entry.count += 1;
    byId.set(cat.id, entry);
  }
  return [...byId.values()].sort((a, b) => b.count - a.count);
}

function categoryPills(logs, catFilter) {
  const cats = deriveLogCategories(logs);
  if (cats.length < 2) return '';
  return `
    <div class="v2-chips place-pills" role="group" aria-label="Filter yoinks by category">
      <button class="v2-chip ${catFilter ? '' : 'chip-active'}" data-place-pill="">All (${logs.length})</button>
      ${cats.map((c) => `
        <button class="v2-chip ${catFilter === c.id ? 'chip-active' : ''}" data-place-pill="${c.id}"
          data-pill-slug="${slugify(c.name)}">
          ${categoryIcon(c, 16)}<span>${escapeHtml(c.name)} (${c.count})</span>
        </button>`).join('')}
    </div>`;
}

function detailRows(metadata) {
  const rows = [];
  if (metadata.rating) {
    // V1's floor + half-star (location-panel.js:586-598) — 4.5 is not 5
    const stars = '★'.repeat(Math.floor(metadata.rating)) + (metadata.rating % 1 >= 0.5 ? '½' : '');
    const reviews = metadata.rating_count ? ` (${metadata.rating_count} reviews)` : '';
    rows.push(`<div class="place-detail"><span>Rating</span><span>${metadata.rating} ${stars}${escapeHtml(reviews)}</span></div>`);
  }
  if (metadata.price_level) {
    rows.push(`<div class="place-detail"><span>Price</span><span>${escapeHtml(String(PRICE_MAP[metadata.price_level] || metadata.price_level))}</span></div>`);
  }
  if (metadata.phone) {
    rows.push(`<div class="place-detail"><span>Phone</span><a href="tel:${escapeHtml(metadata.phone)}">${escapeHtml(metadata.phone)}</a></div>`);
  }
  if (metadata.website) {
    // V1 shows the domain, not a generic label (location-panel.js:634-641)
    let host = metadata.website;
    try {
      host = new URL(metadata.website).hostname.replace(/^www\./, '');
    } catch { /* leave as-is */ }
    rows.push(`<div class="place-detail"><span>Website</span><a href="${escapeHtml(metadata.website)}" target="_blank" rel="noopener">${escapeHtml(host)}</a></div>`);
  }
  // hours can arrive as [] (a place with no listed hours) — an empty
  // array is truthy, and rendering it left a dead bare "Hours" label
  const hours = metadata.hours?.length ? metadata.hours : null;
  if (!rows.length && !hours && !metadata.description) return '';
  return `
    <div class="place-details">
      ${metadata.description ? `<p class="place-desc">${escapeHtml(metadata.description)}</p>` : ''}
      ${rows.join('')}
      ${hours ? `
      <details class="place-hours">
        <summary>Hours</summary>
        <ul>${hours.map((h) => `<li>${escapeHtml(h)}</li>`).join('')}</ul>
      </details>` : ''}
    </div>`;
}

const COLUMN_TO_BANNER = {
  closed_permanently: 'CLOSED_PERMANENTLY',
  closed_temporarily: 'CLOSED_TEMPORARILY',
};

/**
 * What this page should SAY about the place being open, resolved from the
 * two sources that can disagree. Exported for tests.
 *
 * Normally metadata wins: an anon viewer can't write the column, so a fresh
 * Google answer in hand is more current than a stale stored one.
 *
 * But an ADMIN PIN (metadata.status_source === 'admin',
 * supabase/migrations/20260810_admin_status_override.sql) outranks both —
 * it exists precisely for places Google still calls OPERATIONAL, and reading
 * metadata first meant a hand-stamped closure showed no banner at all while
 * the map pin, feed card and route planner (which all read the column) said
 * closed. The place's own page was the one surface that disagreed.
 */
export function resolveStatus(metadata, location) {
  if (metadata?.status_source === 'admin') {
    return COLUMN_TO_BANNER[location?.status] || 'OPERATIONAL';
  }
  return metadata?.business_status || COLUMN_TO_BANNER[location?.status];
}

/**
 * V1's closed-business banner, as a plain-words stamp. The persisted
 * locations.status column (M8 SQL batch) backstops metadata that hasn't
 * refreshed on this view.
 */
function statusBanner(metadata, location) {
  const status = resolveStatus(metadata, location);
  if (status === 'CLOSED_PERMANENTLY') {
    // V1's warmer framing (location-panel.js:673-718): the page still
    // exists because the memories do
    return `<div class="place-closed">Permanently closed
      <small>The yoinks here are memories from when it was still open.</small></div>`;
  }
  if (status === 'CLOSED_TEMPORARILY') {
    return `<div class="place-closed place-closed-temp">Temporarily closed
      <small>Check back later or contact them directly.</small></div>`;
  }
  return '';
}

/**
 * The community's closure report (2026-08-09): the person standing in
 * front of the rubble knows before Google does. Signed in only (the
 * RPCs behind it require auth), hidden once the page already wears a
 * closed stamp, and a standing report shows as a note instead of the
 * button — one report is enough.
 */
function reportRow(store, location, metadata) {
  if (!store.get().user) return '';
  const status = resolveStatus(metadata, location);
  if (status === 'CLOSED_PERMANENTLY' || status === 'CLOSED_TEMPORARILY') return '';
  if (metadata?.reported_closed_at) {
    return '<p class="place-report-note">Reported as closed — thanks, we’re checking on it.</p>';
  }
  return `<button type="button" class="place-report" data-report-gone>
    Is this place gone for good? Report it closed</button>`;
}

function mapLink(location, metadata) {
  if (location.lat == null || location.lng == null) return '';
  // One map link is enough — Google Maps itself offers directions from
  // here, so a separate "Get directions" button just added clutter.
  const view = metadata.google_maps_url ||
    `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
  return `<a class="v2-btn-more" href="${escapeHtml(view)}" target="_blank" rel="noopener">Find on map</a>`;
}

/**
 * Save controls and the map link share one wrapping row (2026-07-27
 * tidy-up — two stacked button rows read as clutter). CSS flattens the
 * save controls' own wrapper into this row with display: contents.
 */
function actionsRow(...pieces) {
  const present = pieces.filter(Boolean);
  return present.length ? `<div class="place-actions">${present.join('')}</div>` : '';
}

function coVisitedStrip(coVisited) {
  if (!coVisited?.length) return '';
  return `
    <section class="place-friends" aria-label="Also popular with visitors">
      <h3 class="friends-title">Visitors here also went to…</h3>
      <div class="friends-row">
        ${coVisited.map((loc) => `
          <button type="button" class="friend-chip" data-covisit-id="${loc.id}">
            <span>${escapeHtml(loc.name)}</span>
          </button>`).join('')}
      </div>
    </section>`;
}

export function renderPlace(el, location, logs, store, metadata = {}, coVisited = [], catFilter = null) {
  const stats = computeStats(logs);
  const headCats = deriveLogCategories(logs);
  const address = location.formatted_address ||
    metadata.formatted_address || location.address || ''; // V1's fallback chain (location-panel.js:350)
  const shownLogs = catFilter ? logs.filter((l) => l.categories?.id === catFilter) : logs;
  const activeCat = catFilter ? deriveLogCategories(logs).find((c) => c.id === catFilter) : null;

  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  el.innerHTML = `
    <nav class="v2-back" aria-label="Back">
      <a href="${basePath}/" data-route="discover">← Discover</a>
      <span class="yoink-share-row">
        ${shareMenu(
          sharePhotosButton(),
          shareButton(location.display_name || 'A place')
        )}
      </span>
    </nav>
    <article class="v2-place">
      ${heroHtml(logs, metadata)}
      <div class="place-body">
        ${statusBanner(metadata, location)}
        <div class="place-head">
          <div>
            <h2 class="place-title">${escapeHtml(location.display_name || 'Unknown place')}</h2>
            ${address ? `<p class="place-addr">${escapeHtml(address)}</p>` : ''}
          </div>
          ${headCats.length ? `
          <div class="place-head-cats" role="group" aria-label="Categories yoinked here">
            ${headCats.map((c) => `
            <button type="button" class="cat-chip" data-head-cat="${slugify(c.name)}"
              aria-label="See all ${escapeHtml(c.name)} places">
              ${categoryIcon(c, 16)}<span>${escapeHtml(c.name)}</span>
            </button>`).join('')}
          </div>` : ''}
        </div>
        <div class="place-stamps">
          <div class="pstamp ps-red"><span class="big">${stats.yoinks}</span><span class="lbl">Yoink${stats.yoinks === 1 ? '' : 's'}</span></div>
          <div class="pstamp ps-blue"><span class="big">${stats.explorers}</span><span class="lbl">Explorer${stats.explorers === 1 ? '' : 's'}</span></div>
        </div>
        ${detailRows(metadata)}
        ${actionsRow(saveControls(store, location.id), mapLink(location, metadata))}
        ${reportRow(store, location, metadata)}
        ${friendsStrip(logs, store)}
        ${coVisitedStrip(coVisited)}
        <a class="v2-btn-leather place-log-btn" data-log-here
          href="${basePath}/log/${location.id}">Log a yoink here</a>
        <section class="place-logs" aria-label="Yoinks at this place">
          <h3 class="logs-title">${activeCat
            ? `${escapeHtml(activeCat.name)} yoinks <span class="geo-count">${shownLogs.length}</span>`
            : 'From the journals…'}</h3>
          ${categoryPills(logs, catFilter)}
          ${shownLogs.map(logEntry).join('') || '<p class="v2-empty">No yoinks here yet.</p>'}
        </section>
      </div>
    </article>
  `;
}
